import { Component, DestroyRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MenuAction } from 'src/app/shared/enums/menu.action';
import { ContextMenuItem } from '../../collection-list-widget/classes/context-menu.item';
import { ContextMenuItemService } from '../../collection-list-widget/services/context-menu-item.service';
import { Store } from '@ngrx/store';
import { workspaceSidebarFeature } from '../../../../../store/features/workspace-sidebar/workspace-sidebar-feature';
import {
  ContextMenuComponent as KendoContextMenuComponent,
  ContextMenuModule,
  ContextMenuSelectEvent,
} from '@progress/kendo-angular-menu';
import {
  ActionSheetComponent as KendoActionSheetComponent,
  ActionSheetModule,
} from '@progress/kendo-angular-navigation';
import { Subject } from 'rxjs';
import { IconComponent } from '../../../../../shared/components/ui/icon/icon.component';
import { CollectionListDataInstance } from '../../../../../shared/interfaces/collection-list-data-instance';
import { GridService } from '../../collection-list-widget/services/grid/grid.service';
import * as Sentry from '@sentry/angular';
import { GridContextPermissions } from '../../collection-list-widget/classes/grid-context-permissions';
import { Dialog } from '@angular/cdk/dialog';
import { EditOnlineService } from '../../../../../core/services/file-upload/edit-online.service';
import { DocumentEventsService } from 'src/app/shared/services/document-events/document-events.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FileUploadEditOnlineModalComponentMode,
} from '../../../../../../models/ts/file-upload-edit-online-modal-component-mode';

/**
 * kendo contextmenu containing all actions for an instance.
 */
@Component({
  selector: 'bizz-archive-context-menu',
  standalone: true,
  imports: [CommonModule, ContextMenuModule, IconComponent, ActionSheetModule],
  templateUrl: '../context-menu.component.html',
  styleUrls: ['../context-menu.component.scss'],
})
@Sentry.TraceClass({ name: 'ArchiveContextMenuComponent' })
export class ArchiveContextMenuComponent implements OnDestroy {
  @ViewChild('menu', { static: false }) public menu!: KendoContextMenuComponent;

  @ViewChild('actionsheet', { static: true }) public actionsheet!: KendoActionSheetComponent;

  @Input() public dataItem: CollectionListDataInstance | any;
  @Input() public permissions: GridContextPermissions | undefined;
  @Input() public anchor: any;
  @Input() public isDraft: boolean;
  @Input() public useSeparators: boolean;

  public menuItems: Partial<ContextMenuItem>[] = [];
  private workspaceIdSignal = this.store$.selectSignal(workspaceSidebarFeature.selectID);
  public isMobileView = false;

  /**
   * Subject used to unsubscribe from all subscriptions when the component is destroyed.
   */
  private destroy: Subject<void> = new Subject<void>();

  public constructor(private gridService: GridService,
                     private contextMenuItemService: ContextMenuItemService,
                     private store$: Store,
                     private dialog: Dialog,
                     private editOnlineService: EditOnlineService,
                     private documentEventsService: DocumentEventsService,
                     private destroyRef: DestroyRef) {

    this.documentEventsService.isMobile.pipe(
      takeUntilDestroyed(this.destroyRef)).subscribe((isMobile: boolean) => {
        this.isMobileView = isMobile;
      },
    );
  }

  @Sentry.TraceMethod({ name: 'open' })
  public open(): void {
    if (!this.dataItem || !this.permissions || !this.anchor) {
      throw new Error('Missing data for context menu');
    }

    this.menuItems = this.contextMenuItemService.getMenuItems(this.permissions, this.dataItem, this.isMobileView, this.useSeparators);

    if (this.isMobileView) {
      this.actionsheet.toggle(true);
    } else this.menu.show(this.anchor);
  }

  @Sentry.TraceMethod({ name: 'close' })
  public close(): void {
    return;
  }

  public onContextMenuSelectEvent($event: ContextMenuSelectEvent): void {
    if ($event.item.items && $event.item.items.length > 0)
      return; // Do not handle selection if the item has sub-items
    this.onItemSelected($event.item);
  }

  public onActionSheetItemClick(item: Partial<ContextMenuItem>): void {
    if (item.items && item.items.length > 0) {
      item.expanded = !item.expanded;
      return; // Do not handle selection if the item has sub-items
    }
    this.onItemSelected(item);
    this.close();
  }

  public onItemSelected(item: Partial<ContextMenuItem>): void {
    if (!item || typeof item.action === 'undefined') return;
    this.handleMenuAction(item.action);
    if (this.isMobileView) {
      this.actionsheet.toggle(false);
    } else this.menu.hide();
  }

  private handleMenuAction(action: MenuAction): void {
    switch (action) {
      case MenuAction.ConsultRecord:
        this.gridService.readInstance(this.dataItem.CollectionsID, this.dataItem.ID, this.isDraft && this.dataItem.DraftsID > 0 ? this.dataItem.DraftsID : this.dataItem.VersionsID, this.dataItem.GuidChecksum, 0, this.dataItem.OriginalFoldersID, undefined, undefined, true);
        break;
      case MenuAction.DownloadFile:
        this.gridService.downloadArchiveFile(this.dataItem);
        break;
      case MenuAction.FlowStatus:
        this.gridService.openFlowStatusPage(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID, this.dataItem.MethodType ?? this.dataItem.DocumentFlow, this.workspaceIdSignal(), false, true);
        break;
      case MenuAction.Monitor:

        this.gridService.openMonitorModal(this.dataItem.CollectionsID, this.dataItem.ID, this.dataItem.VersionsID);
        break;
      case MenuAction.FileHistory:
        this.gridService.openFileHistoryModal(this.dataItem);
        break;
      case MenuAction.ViewOnline:
        this.editOnlineService.openViewOnlineWithoutDraftValidation(this.dataItem, FileUploadEditOnlineModalComponentMode.View);
        break;
      default:
        throw new Error('Unhandled context menu action');
    }
  }

  @Sentry.TraceMethod({ name: 'ngOnDestroy' })
  public ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.unsubscribe();
  }
}

